import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'

import { MAX_CODEC_INPUT_CHARS } from '../../lib/codecs'
import LinedTextArea, { LINED_TEXT_AREA_FILL_CLASS, TOOL_WORKSPACE_GRID_LG_CLASS } from './LinedTextArea'
import { type ToolAbout, TOOL_CENTER_RAIL_RULE_CLASS, ToolAboutSection, ToolIoChip } from './toolChrome'

export type ToolMode = 'encode' | 'decode'

const SWAP_RAIL_ICON_CLASS =
  'material-symbols-outlined text-on-surface-variant transition-colors hover:text-primary'

/** Keeps layout; use with always-mounted rail pieces (mode/options/swap) on encode-only or single-action tools. */
const TOOL_CENTER_RAIL_HIDDEN = 'invisible pointer-events-none select-none'

function CenterRailSwap({
  onSwap,
  title,
  inactive,
}: {
  onSwap: () => void
  title: string
  inactive?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onSwap}
      title={title}
      disabled={inactive}
      tabIndex={inactive ? -1 : undefined}
      className={SWAP_RAIL_ICON_CLASS}
    >
      swap_vert
    </button>
  )
}

/** Shared by input chrome and output header so rows stay the same height. */
export const TOOL_IO_HEADER_ROW =
  'flex min-h-8 shrink-0 flex-wrap items-center justify-between gap-2 leading-none'

export function ToolSettingsPopover({
  children,
  inactive,
}: {
  children: ReactNode
  inactive?: boolean
}) {
  const [open, setOpen] = useState(false)
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (inactive) setOpen(false)
  }, [inactive])

  useEffect(() => {
    if (!open) return
    const down = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', down)
    return () => document.removeEventListener('mousedown', down)
  }, [open])

  useEffect(
    () => () => {
      if (leaveTimer.current) clearTimeout(leaveTimer.current)
    },
    [],
  )

  const clearLeave = () => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current)
      leaveTimer.current = null
    }
  }

  const scheduleClose = () => {
    clearLeave()
    leaveTimer.current = setTimeout(() => setOpen(false), 200)
  }

  return (
    <div
      ref={rootRef}
      className="relative shrink-0"
      onMouseEnter={() => {
        if (inactive) return
        clearLeave()
        setOpen(true)
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        disabled={inactive}
        tabIndex={inactive ? -1 : undefined}
        className="inline-flex size-9 items-center justify-center rounded-md border border-outline-variant/40 bg-surface-container-highest text-on-surface-variant transition-colors hover:border-outline hover:bg-surface-container-high hover:text-on-surface disabled:opacity-100"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Options"
        title="Options"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="material-symbols-outlined text-[20px] leading-none">tune</span>
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Tool options"
          className="absolute right-0 top-full z-[60] mt-1 max-h-[min(70vh,28rem)] w-[min(22rem,calc(100vw-2rem))] overflow-y-auto overscroll-contain rounded-lg border border-outline-variant/30 bg-surface-container-highest p-3 shadow-2xl"
          onMouseEnter={clearLeave}
          onMouseLeave={scheduleClose}
        >
          <div className="flex flex-col gap-3">{children}</div>
        </div>
      ) : null}
    </div>
  )
}

export type ToolWorkspaceProps = {
  heading: string
  tagline?: string
  /** Left side of the input panel header (e.g. INPUT chip). */
  inputHeaderStart?: ReactNode
  /** Inline algorithm / codec control (compact select), placed before Options. */
  algorithmSelector?: ReactNode
  /** Options-popover content (e.g. PanelSwitch rows; center rail, not the input header). */
  toolSettings?: ReactNode
  /** Right side of the input panel header (e.g. format hint). */
  inputHeaderEnd?: ReactNode
  /**
   * When true, the input pane is omitted (rail + output only). Use for generate-only tools that do not read
   * user input (e.g. one-shot generators). Do not pass `inputColumn` / input header props in that case.
   */
  omitInputColumn?: boolean
  /** Main input region (editors only; outer padding/chrome is provided here). Omit when `omitInputColumn`. */
  inputColumn?: ReactNode
  mode: ToolMode
  setMode: (m: ToolMode) => void
  encodeOnly: boolean
  swapDisabled: boolean
  swapTitle: string
  onSwap: () => void
  onExecute: () => void
  executeBusy: boolean
  /** For tools that run automatically (e.g. on every input change) instead of on a manual trigger. */
  hideExecute?: boolean
  output: string
  onCopy: () => void
  error: string | null
  statsCharLabel?: string
  statsCharValue: string
  statsAlgorithm: string
  statsOutputLen: string
  statsModeLabel: string
  outputSlot?: ReactNode
  outputLineNumbers?: boolean
  lineWrap?: boolean
  /** Collapsed by default. Short description plus links for libraries and browser APIs this tool uses. */
  about: ToolAbout
}

export default function ToolWorkspace({
  heading,
  tagline = 'Status: ready // buffer: clear',
  inputHeaderStart,
  algorithmSelector,
  toolSettings,
  inputHeaderEnd,
  omitInputColumn = false,
  inputColumn,
  mode,
  setMode,
  encodeOnly,
  swapDisabled,
  swapTitle,
  onSwap,
  onExecute,
  executeBusy,
  hideExecute = false,
  output,
  onCopy,
  error,
  statsCharLabel = 'Char count',
  statsCharValue,
  statsAlgorithm,
  statsOutputLen,
  statsModeLabel,
  outputSlot,
  outputLineNumbers = false,
  lineWrap = false,
  about,
}: ToolWorkspaceProps) {
  const wrapLines = Boolean(lineWrap)
  const showInputChrome =
    !omitInputColumn &&
    (inputHeaderStart != null || algorithmSelector != null || inputHeaderEnd != null)
  const hideModeRail = encodeOnly
  const hideOptionsRail = toolSettings == null
  const hideSwapRail = swapDisabled
  const hideExecuteRail = hideExecute

  return (
    <div className="space-y-8">
      <div className="flex flex-col justify-between gap-4 border-b border-outline-variant/20 pb-4 md:flex-row md:items-end">
        <div>
          <h1 className="font-headline text-3xl font-bold tracking-tighter text-on-surface uppercase">{heading}</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-on-surface-variant uppercase">{tagline}</p>
        </div>
        <div className="text-left md:text-right">
          <p className="max-w-md text-right font-mono text-[10px] leading-relaxed tracking-widest text-on-surface-variant uppercase">
            Runs entirely in your browser. Max {MAX_CODEC_INPUT_CHARS.toLocaleString()} chars per run.
          </p>
        </div>
      </div>

      <div
        className={`grid min-h-0 w-full min-w-0 grid-cols-1 auto-rows-[minmax(0,auto)] rounded-xl border border-outline-variant/20 shadow-2xl max-lg:overflow-visible lg:overflow-visible ${
          omitInputColumn ? 'lg:grid-cols-[auto_minmax(0,1fr)]' : 'lg:grid-cols-[1fr_auto_1fr]'
        } ${TOOL_WORKSPACE_GRID_LG_CLASS} [&>*:not(.tool-workspace__center-rail)]:min-h-0 [&>*]:min-w-0`}
      >
        {omitInputColumn ? null : (
          <div className="flex min-h-0 min-w-0 flex-col lg:h-full lg:min-h-0 lg:overflow-y-auto">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 bg-surface-container-low p-6">
              {showInputChrome ? (
                <div className={TOOL_IO_HEADER_ROW}>
                  <div className="flex min-w-0 items-center gap-2">{inputHeaderStart}</div>
                  <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-x-2 gap-y-1">
                    {algorithmSelector}
                    {inputHeaderEnd}
                  </div>
                </div>
              ) : null}
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">{inputColumn}</div>
            </div>
          </div>
        )}

        <div className="tool-workspace__center-rail relative z-20 flex w-full max-lg:flex-nowrap flex-row flex-wrap items-center justify-center gap-3 border-y border-outline-variant/10 bg-surface-container-high py-5 max-lg:overflow-visible sm:max-lg:gap-4 lg:grid lg:h-full lg:min-h-0 lg:w-24 lg:grid-rows-[minmax(min-content,1fr)_auto_minmax(min-content,1fr)] lg:justify-items-stretch lg:gap-0 lg:border-x lg:border-y-0 lg:overflow-visible lg:py-8">
          <div className="flex shrink-0 flex-row items-center justify-center gap-6 max-lg:w-auto lg:h-full lg:min-h-0 lg:flex-col lg:gap-0 lg:pb-2">
            <div className="hidden min-h-0 w-full lg:flex lg:flex-1" aria-hidden />
            <div className="flex shrink-0 flex-row items-center justify-center gap-6 max-lg:w-auto lg:w-full lg:flex-col lg:items-center lg:gap-6">
              <div
                className={`flex w-auto max-w-full flex-col items-center gap-2 max-lg:flex-row max-lg:items-center lg:w-full lg:justify-start ${
                  hideModeRail ? TOOL_CENTER_RAIL_HIDDEN : ''
                }`}
                aria-hidden={hideModeRail ? true : undefined}
              >
                <div className="flex flex-col items-center gap-2 max-lg:flex-row max-lg:gap-2">
                  <div className="flex w-full max-lg:w-auto items-center justify-center overflow-hidden rounded-sm bg-surface-container-high lg:min-h-[4.5rem]">
                    <span className="font-mono text-[10px] leading-none text-on-surface-variant uppercase lg:[text-orientation:upright] lg:[writing-mode:vertical-rl]">
                      Mode
                    </span>
                  </div>
                  <div className="flex flex-col gap-px overflow-hidden rounded-md max-lg:flex-row max-lg:rounded-md">
                    <button
                      type="button"
                      disabled={hideModeRail}
                      tabIndex={hideModeRail ? -1 : undefined}
                      onClick={() => setMode('encode')}
                      className={`flex h-10 w-12 items-center justify-center font-mono text-[9px] font-bold tracking-tighter ${
                        mode === 'encode'
                          ? 'bg-primary-container text-on-primary'
                          : 'bg-surface-container-highest text-on-surface-variant hover:bg-surface-variant'
                      } disabled:opacity-100`}
                    >
                      ENC
                    </button>
                    <button
                      type="button"
                      disabled={hideModeRail}
                      tabIndex={hideModeRail ? -1 : undefined}
                      onClick={() => setMode('decode')}
                      className={`flex h-10 w-12 items-center justify-center font-mono text-[9px] font-bold tracking-tighter ${
                        mode === 'decode'
                          ? 'bg-primary-container text-on-primary'
                          : 'bg-surface-container-highest text-on-surface-variant hover:bg-surface-variant'
                      } disabled:opacity-100`}
                    >
                      DEC
                    </button>
                  </div>
                </div>
                <div className={TOOL_CENTER_RAIL_RULE_CLASS} />
              </div>
            </div>
          </div>
          <div
            className={`flex w-auto max-w-full shrink-0 items-center justify-center max-lg:px-1 lg:w-full lg:min-h-0 lg:justify-self-center lg:px-0 ${
              hideExecuteRail ? TOOL_CENTER_RAIL_HIDDEN : ''
            }`}
            aria-hidden={hideExecuteRail ? true : undefined}
          >
            <button
              type="button"
              disabled={executeBusy || hideExecuteRail}
              tabIndex={hideExecuteRail ? -1 : undefined}
              onClick={() => void onExecute()}
              className="group relative flex size-14 items-center justify-center rounded-lg bg-primary-container text-on-primary shadow-[0_4px_0_0_#9a4000] transition-all enabled:active:translate-y-0.5 enabled:active:shadow-none disabled:cursor-wait disabled:opacity-60"
              title="Execute"
            >
              <span className="material-symbols-outlined text-3xl">bolt</span>
              <span className="pointer-events-none absolute -top-8 rounded-sm bg-tertiary-fixed px-2 py-0.5 font-mono text-[9px] font-bold text-on-tertiary-fixed opacity-0 transition-opacity group-hover:opacity-100">
                EXECUTE
              </span>
            </button>
          </div>
          <div className="flex w-auto max-w-full shrink-0 flex-col items-center justify-center gap-6 max-lg:flex-row lg:h-full lg:min-h-0 lg:pb-2">
            <div className="hidden min-h-0 w-full lg:flex lg:flex-1" aria-hidden />
            <div className="flex shrink-0 flex-row flex-wrap items-center justify-center gap-4 max-lg:w-auto sm:max-lg:gap-6 lg:w-full lg:flex-col lg:items-center lg:gap-6">
              <div
                className={`flex flex-col items-center gap-6 max-lg:flex-row max-lg:gap-3 ${hideOptionsRail ? TOOL_CENTER_RAIL_HIDDEN : ''}`}
                aria-hidden={hideOptionsRail ? true : undefined}
              >
                <div className={TOOL_CENTER_RAIL_RULE_CLASS} />
                <ToolSettingsPopover inactive={hideOptionsRail}>{toolSettings ?? null}</ToolSettingsPopover>
                <div className={TOOL_CENTER_RAIL_RULE_CLASS} />
              </div>
              <div className={hideSwapRail ? TOOL_CENTER_RAIL_HIDDEN : ''} aria-hidden={hideSwapRail ? true : undefined}>
                <CenterRailSwap onSwap={onSwap} title={swapTitle} inactive={hideSwapRail} />
              </div>
            </div>
          </div>
        </div>

        <div className="flex h-full min-h-0 min-w-0 flex-col gap-4 overflow-hidden bg-surface-container-low p-6 lg:h-full">
          <div className={TOOL_IO_HEADER_ROW}>
            <ToolIoChip>OUTPUT</ToolIoChip>
            <div className="flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                title="Copy result"
                aria-label="Copy result to clipboard"
                onClick={() => void onCopy()}
                disabled={!output}
                className="m-0 inline-flex h-2.5 w-4 shrink-0 items-center justify-center border-0 bg-transparent p-0 text-on-surface-variant outline-none ring-0 transition-colors hover:text-primary-container focus-visible:ring-2 focus-visible:ring-outline/40 disabled:opacity-40"
              >
                <span className="material-symbols-outlined pointer-events-none origin-center scale-110 text-sm leading-none">
                  content_copy
                </span>
              </button>
              <span className="flex items-center gap-1 font-mono text-[10px] leading-none tracking-widest text-on-surface-variant uppercase">
                <span className="size-1 shrink-0 animate-pulse rounded-full bg-primary-container" />
                Result
              </span>
            </div>
          </div>
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden lg:min-h-0">
            {outputSlot ?? (
              outputLineNumbers ? (
                <LinedTextArea
                  readOnly
                  fillHeight
                  value={output}
                  rows={14}
                  placeholder="Output appears here…"
                  aria-label="Tool output"
                  wrapLines={wrapLines}
                />
              ) : (
                <textarea
                  readOnly
                  value={output}
                  rows={14}
                  wrap={wrapLines ? 'soft' : 'off'}
                  className={`form-well grid-bg ${LINED_TEXT_AREA_FILL_CLASS} min-h-0 resize-none p-4 font-mono text-sm text-on-surface ${
                    wrapLines
                      ? 'overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-words'
                      : 'overflow-auto whitespace-pre'
                  }`}
                  placeholder="Output appears here…"
                />
              )
            )}
          </div>
        </div>
      </div>

      {error ? <p className="font-mono text-sm text-error">{error}</p> : null}

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="flex flex-col rounded-md border border-outline-variant/10 bg-surface-container-lowest p-4">
          <span className="mb-1 font-mono text-[9px] text-on-surface-variant uppercase">{statsCharLabel}</span>
          <span className="font-mono text-lg text-on-surface">{statsCharValue}</span>
        </div>
        <div className="flex flex-col rounded-md border border-outline-variant/10 bg-surface-container-lowest p-4">
          <span className="mb-1 font-mono text-[9px] text-on-surface-variant uppercase">Algorithm</span>
          <span className="font-mono text-lg text-on-surface">{statsAlgorithm}</span>
        </div>
        <div className="flex flex-col rounded-md border border-outline-variant/10 bg-surface-container-lowest p-4">
          <span className="mb-1 font-mono text-[9px] text-on-surface-variant uppercase">Output length</span>
          <span className="font-mono text-lg text-on-surface">{statsOutputLen}</span>
        </div>
        <div className="flex flex-col rounded-md border border-outline-variant/10 bg-surface-container-lowest p-4">
          <span className="mb-1 font-mono text-[9px] text-on-surface-variant uppercase">Mode</span>
          <span className="font-mono text-lg text-primary-container">{statsModeLabel}</span>
        </div>
      </div>

      <ToolAboutSection about={about} />
    </div>
  )
}
